
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface SubscriptionStatus {
  status: "active" | "inactive";
  plan: "monthly" | "yearly" | null;
  nextBillingDate: string | null;
  subscriptionId: string | null;
  isCancelled: boolean;
}

export const useSubscriptionStatus = (userId: string | undefined) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    status: "inactive",
    plan: null,
    nextBillingDate: null,
    subscriptionId: null,
    isCancelled: false,
  });
  
  const [loading, setLoading] = useState(true);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    try {
      // Get subscription data
      console.log("Fetching subscription for user:", userId);
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('strategy_subscriptions')
        .select('*')
        .eq('user_id', userId);

      if (subscriptionError) {
        console.error("Error fetching subscription:", subscriptionError);
        toast.error("Failed to load subscription information");
        setSubscriptionStatus({
          status: "inactive",
          plan: null,
          nextBillingDate: null,
          subscriptionId: null,
          isCancelled: false,
        });
        return;
      }
      
      console.log("Raw subscription data:", subscriptionData);
      
      // Check for any subscription (active or with future billing date)
      if (Array.isArray(subscriptionData) && subscriptionData.length > 0) {
        // Find a subscription that is either active or has a future billing date
        const currentDate = new Date();
        const validSubscription = subscriptionData.find(sub => {
          const nextBillingDate = sub.next_billing_date ? new Date(sub.next_billing_date) : null;
          return sub.active || (nextBillingDate && currentDate < nextBillingDate);
        });
        
        if (validSubscription) {
          console.log("Found valid subscription:", validSubscription);
          
          // Check if it's cancelled but still valid
          const isCancelled = validSubscription.active === false && validSubscription.next_billing_date !== null;
          
          // Ensure the plan is one of the allowed types
          let planValue: "monthly" | "yearly" | null = null;
          if (validSubscription.plan === "monthly" || validSubscription.plan === "yearly") {
            planValue = validSubscription.plan;
          } else {
            console.log(`Invalid plan value "${validSubscription.plan}", defaulting to monthly`);
            planValue = "monthly";
          }
          
          setSubscriptionStatus({
            status: "active",
            plan: planValue,
            nextBillingDate: validSubscription.next_billing_date,
            subscriptionId: validSubscription.subscription_id,
            isCancelled: isCancelled,
          });
        } else {
          console.log("No valid subscription found");
          setSubscriptionStatus({
            status: "inactive",
            plan: null,
            nextBillingDate: null,
            subscriptionId: null,
            isCancelled: false,
          });
        }
      } else {
        console.log("No subscription data found");
        setSubscriptionStatus({
          status: "inactive",
          plan: null,
          nextBillingDate: null,
          subscriptionId: null,
          isCancelled: false,
        });
      }
    } catch (error) {
      console.error("Unexpected error in fetchSubscriptionStatus:", error);
      toast.error("Error loading subscription data");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  return {
    subscriptionStatus,
    loading,
    refreshSubscriptionStatus: fetchSubscriptionStatus
  };
};
